/**
 * Amorphous P2P Bootstrap - Plain JavaScript
 *
 * Handles peer discovery via:
 * - BroadcastChannel (same-origin tabs) - full signaling
 * - CGI peer directory (cross-origin) - bootstrap peers only
 *
 * For cross-origin signaling, once connected to bootstrap peers,
 * use PeerManager's relay capability to reach other peers.
 *
 * Supports universe namespacing - each universe has isolated peer discovery
 */

const DEFAULT_UNIVERSE = 'amorphous';
const PEER_TIMEOUT_MS = 30000;

// Adaptive polling intervals based on connected peer count
const POLL_INTERVAL_NO_PEERS = 5000;      // 5s when no peers (aggressive discovery)
const POLL_INTERVAL_FEW_PEERS = 15000;    // 15s when 1-2 peers (moderate discovery)
const POLL_INTERVAL_SOME_PEERS = 30000;   // 30s when 3-5 peers (standard)
const POLL_INTERVAL_MANY_PEERS = 60000;   // 60s when 6+ peers (relaxed)

/**
 * Signal message types
 */
export class Signal {
  constructor(type, payload = {}, universe = DEFAULT_UNIVERSE) {
    this.type = type;
    this.payload = payload;
    this.universe = universe;
    this.timestamp = Date.now();
  }

  static announce(peerId, universe = DEFAULT_UNIVERSE) {
    return new Signal('announce', { peer_id: peerId }, universe);
  }

  static disconnect(peerId, universe = DEFAULT_UNIVERSE) {
    return new Signal('disconnect', { peer_id: peerId }, universe);
  }

  static offer(from, to, sdp, universe = DEFAULT_UNIVERSE) {
    return new Signal('offer', { from, to, sdp }, universe);
  }

  static answer(from, to, sdp, universe = DEFAULT_UNIVERSE) {
    return new Signal('answer', { from, to, sdp }, universe);
  }

  static iceCandidate(from, to, candidate, sdpMid, sdpMLineIndex, universe = DEFAULT_UNIVERSE) {
    return new Signal('ice_candidate', { from, to, candidate, sdp_mid: sdpMid, sdp_m_line_index: sdpMLineIndex }, universe);
  }

  static peerList(peers, universe = DEFAULT_UNIVERSE) {
    return new Signal('peer_list', { peers }, universe);
  }

  toJSON() {
    return JSON.stringify({ type: this.type, payload: this.payload, universe: this.universe, timestamp: this.timestamp });
  }

  static fromJSON(json) {
    const data = typeof json === 'string' ? JSON.parse(json) : json;
    const signal = new Signal(data.type, data.payload, data.universe || DEFAULT_UNIVERSE);
    signal.timestamp = data.timestamp;
    return signal;
  }

  get sourcePeer() {
    return this.payload.peer_id || this.payload.from;
  }

  get targetPeer() {
    return this.payload.to;
  }
}

/**
 * Peer info tracking
 */
class PeerInfo {
  constructor(id, source = 'unknown') {
    this.id = id;
    this.state = 'discovered';
    this.lastSeen = Date.now();
    this.source = source; // 'local' (BroadcastChannel) or 'server' (bootstrap)
  }

  touch() {
    this.lastSeen = Date.now();
  }
}

/**
 * Peer Registry - manages peer discovery and signaling
 *
 * Discovery modes:
 * - Local (BroadcastChannel): Full signaling support for same-origin tabs
 * - Server (CGI): Bootstrap peer list only, no signal relay
 *
 * For remote signaling to non-local peers, use PeerManager's relay capability
 * after connecting to bootstrap peers.
 */
export class PeerRegistry {
  constructor(myId, options = {}) {
    this.myId = myId;
    this.universeId = options.universeId || DEFAULT_UNIVERSE;
    this.peers = new Map();
    this.onSignal = null;
    this.onBootstrapPeers = null; // Callback when bootstrap peers discovered
    this.channel = null;
    this.serverUrl = options.serverUrl || null;
    this.pollInterval = null;
    this.currentPollIntervalMs = POLL_INTERVAL_NO_PEERS;
    this.connectedPeerCount = 0;

    this._initBroadcastChannel();

    if (this.serverUrl) {
      this._initServerPolling();
    }
  }

  /**
   * Update the connected peer count (called by usePeers)
   * This adjusts the polling frequency based on how many peers we have
   */
  setConnectedPeerCount(count) {
    const oldCount = this.connectedPeerCount;
    this.connectedPeerCount = count;

    // Determine new poll interval based on peer count
    let newInterval;
    if (count === 0) {
      newInterval = POLL_INTERVAL_NO_PEERS;
    } else if (count <= 2) {
      newInterval = POLL_INTERVAL_FEW_PEERS;
    } else if (count <= 5) {
      newInterval = POLL_INTERVAL_SOME_PEERS;
    } else {
      newInterval = POLL_INTERVAL_MANY_PEERS;
    }

    // Only restart interval if it changed significantly
    if (newInterval !== this.currentPollIntervalMs) {
      console.log(`[Bootstrap] Adjusting poll interval: ${this.currentPollIntervalMs}ms → ${newInterval}ms (${count} peers)`);
      this.currentPollIntervalMs = newInterval;
      this._restartServerPolling();
    }
  }

  _initBroadcastChannel() {
    // Universe-specific channel name for local tab discovery
    const channelName = `aos-signal-${this.universeId}`;
    try {
      this.channel = new BroadcastChannel(channelName);
      this.channel.onmessage = (event) => {
        this._handleLocalMessage(event.data);
      };
    } catch (e) {
      console.warn('[Bootstrap] BroadcastChannel not available:', e);
    }
  }

  _initServerPolling() {
    // Poll server for bootstrap peers (initial poll is immediate)
    this.pollInterval = setInterval(() => this._pollServer(), this.currentPollIntervalMs);
    // Initial poll
    this._pollServer();
  }

  _restartServerPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    if (this.serverUrl) {
      this.pollInterval = setInterval(() => this._pollServer(), this.currentPollIntervalMs);
    }
  }

  async _pollServer() {
    if (!this.serverUrl) return;

    try {
      const url = `${this.serverUrl}?peer_id=${encodeURIComponent(this.myId)}&universe=${encodeURIComponent(this.universeId)}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();

        // Process peer list
        if (data.peers && Array.isArray(data.peers)) {
          const newPeers = [];
          for (const peerId of data.peers) {
            if (peerId !== this.myId) {
              if (!this.peers.has(peerId)) {
                this.peers.set(peerId, new PeerInfo(peerId, 'server'));
                newPeers.push(peerId);
              } else {
                this.peers.get(peerId).touch();
              }
            }
          }

          // Notify about new bootstrap peers
          if (newPeers.length > 0 && this.onBootstrapPeers) {
            this.onBootstrapPeers(newPeers);
          }
        }

        // Process queued signals from server
        if (data.signals && Array.isArray(data.signals)) {
          for (const signalData of data.signals) {
            try {
              const signal = Signal.fromJSON(signalData);
              if (signal.sourcePeer !== this.myId) {
                this._handleSignalInternal(signal, 'server');
                if (this.onSignal) {
                  this.onSignal(signal);
                }
              }
            } catch (e) {
              console.warn('[Bootstrap] Failed to parse server signal:', e);
            }
          }
        }
      }
    } catch (e) {
      console.warn('[Bootstrap] Server poll failed:', e);
    }
  }

  _handleLocalMessage(data) {
    try {
      const signal = Signal.fromJSON(data);

      // Skip our own messages
      if (signal.sourcePeer === this.myId) return;

      // Skip if different universe
      if (signal.universe !== this.universeId) return;

      // Update internal peer tracking
      this._handleSignalInternal(signal, 'local');

      // Call callback if set (for offer/answer/ice handling)
      if (this.onSignal) {
        this.onSignal(signal);
      }
    } catch (e) {
      console.warn('[Bootstrap] Failed to parse signal:', e);
    }
  }

  _handleSignalInternal(signal, source = 'unknown') {
    switch (signal.type) {
      case 'announce':
        if (signal.sourcePeer && signal.sourcePeer !== this.myId) {
          if (!this.peers.has(signal.sourcePeer)) {
            this.peers.set(signal.sourcePeer, new PeerInfo(signal.sourcePeer, source));
          } else {
            this.peers.get(signal.sourcePeer).touch();
          }
        }
        break;

      case 'disconnect':
        if (signal.sourcePeer) {
          this.peers.delete(signal.sourcePeer);
        }
        break;

      case 'peer_list':
        if (signal.payload.peers) {
          for (const peerId of signal.payload.peers) {
            if (peerId !== this.myId && !this.peers.has(peerId)) {
              this.peers.set(peerId, new PeerInfo(peerId, source));
            }
          }
        }
        break;

      default:
        // For offer/answer/ice_candidate, update last_seen
        if (signal.sourcePeer && this.peers.has(signal.sourcePeer)) {
          this.peers.get(signal.sourcePeer).touch();
        }
        break;
    }
  }

  /**
   * Broadcast locally via BroadcastChannel only
   * For remote peers, use PeerManager relay
   */
  _broadcastLocal(signal) {
    if (this.channel) {
      try {
        this.channel.postMessage(signal.toJSON());
      } catch (e) {
        console.warn('[Bootstrap] BroadcastChannel send failed:', e);
      }
    }
  }

  /**
   * Send to server (announce/disconnect only)
   */
  async _sendToServer(signal) {
    if (!this.serverUrl) return;

    try {
      await fetch(this.serverUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: signal.toJSON()
      });
    } catch (e) {
      console.warn('[Bootstrap] Server send failed:', e);
    }
  }

  // Public API

  /**
   * Set callback for incoming signals (offer/answer/ice from local tabs)
   */
  setOnSignal(callback) {
    this.onSignal = callback;
  }

  /**
   * Set callback when bootstrap peers are discovered from server
   * Callback receives array of new peer IDs
   */
  setOnBootstrapPeers(callback) {
    this.onBootstrapPeers = callback;
  }

  /**
   * Announce presence (local + server)
   */
  announce() {
    const signal = Signal.announce(this.myId, this.universeId);
    this._broadcastLocal(signal);
    this._sendToServer(signal);
  }

  /**
   * Disconnect (local + server)
   */
  disconnect() {
    const signal = Signal.disconnect(this.myId, this.universeId);
    this._broadcastLocal(signal);
    this._sendToServer(signal);
  }

  /**
   * Send offer (local + server)
   */
  sendOffer(to, sdp) {
    const signal = Signal.offer(this.myId, to, sdp, this.universeId);
    this._broadcastLocal(signal);
    this._sendToServer(signal);
  }

  /**
   * Send answer (local + server)
   */
  sendAnswer(to, sdp) {
    const signal = Signal.answer(this.myId, to, sdp, this.universeId);
    this._broadcastLocal(signal);
    this._sendToServer(signal);
  }

  /**
   * Send ICE candidate (local + server)
   */
  sendIceCandidate(to, candidate, sdpMid, sdpMLineIndex) {
    const signal = Signal.iceCandidate(this.myId, to, candidate, sdpMid, sdpMLineIndex, this.universeId);
    this._broadcastLocal(signal);
    this._sendToServer(signal);
  }

  /**
   * Check if a peer is local (reachable via BroadcastChannel)
   */
  isLocalPeer(peerId) {
    const peer = this.peers.get(peerId);
    return peer && peer.source === 'local';
  }

  /**
   * Get all discovered peer IDs
   */
  getPeerIds() {
    return Array.from(this.peers.keys());
  }

  /**
   * Get local peer IDs (same-origin tabs)
   */
  getLocalPeerIds() {
    return Array.from(this.peers.entries())
      .filter(([_, p]) => p.source === 'local')
      .map(([id]) => id);
  }

  /**
   * Get bootstrap peer IDs (from server)
   */
  getBootstrapPeerIds() {
    return Array.from(this.peers.entries())
      .filter(([_, p]) => p.source === 'server')
      .map(([id]) => id);
  }

  getPeers() {
    return Array.from(this.peers.values()).map(p => ({
      id: p.id,
      state: p.state,
      lastSeen: p.lastSeen,
      source: p.source
    }));
  }

  getPeerCount() {
    return this.peers.size;
  }

  setPeerState(peerId, state) {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.state = state;
      peer.touch();
    }
  }

  cleanupStalePeers() {
    const now = Date.now();
    for (const [id, peer] of this.peers) {
      if (now - peer.lastSeen > PEER_TIMEOUT_MS) {
        this.peers.delete(id);
      }
    }
  }

  destroy() {
    this.disconnect();

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}

// Re-export boot loader
export { BootManifest, BootLoader, parseUniverseFromUrl, quickBoot } from './boot.js';

export default { Signal, PeerRegistry };
